import { ALL_STATUSES, type InvoiceStatus } from "./schema";

/**
 * Filters of the invoice and quote lists, kept in the URL so a filtered list can be bookmarked:
 * `?q=&status=sent,overdue&customer=c1,c2&from=&to=&dueFrom=&dueTo=&min=&max=&currency=&type=`.
 * `type` narrows the invoice list to invoices or credit notes.
 * Dates are days (YYYY-MM-DD), both ends included.
 */
export type ListFilters = {
  q: string;
  statuses: InvoiceStatus[];
  customers: string[];
  from: string;
  to: string;
  dueFrom: string;
  dueTo: string;
  min: string;
  max: string;
  currency: string;
  type: DocumentType;
};

/** Invoices only: "" = both, "invoices" = no credit notes, "credit_notes" = only credit notes. */
export type DocumentType = "" | "invoices" | "credit_notes";
const DOCUMENT_TYPES: DocumentType[] = ["", "invoices", "credit_notes"];

export const EMPTY_FILTERS: ListFilters = {
  q: "",
  statuses: [],
  customers: [],
  from: "",
  to: "",
  dueFrom: "",
  dueTo: "",
  min: "",
  max: "",
  currency: "",
  type: "",
};

/** The URL parameters the filters use (the list's page resets whenever one changes). */
export const FILTER_PARAMS = [
  "q",
  "status",
  "customer",
  "from",
  "to",
  "dueFrom",
  "dueTo",
  "min",
  "max",
  "currency",
  "type",
] as const;

const DAY = /^\d{4}-\d{2}-\d{2}$/;
const ID = /^[a-z0-9]{1,30}$/i;
const day = (value: string | null) => (value && DAY.test(value) ? value : "");
const amount = (value: string | null) =>
  value && value.trim() !== "" && Number.isFinite(Number(value)) ? String(Number(value)) : "";
const list = (value: string | null) => (value ? value.split(",").filter(Boolean) : []);

export function parseFilters(params: URLSearchParams): ListFilters {
  const currency = (params.get("currency") ?? "").toUpperCase();
  return {
    q: (params.get("q") ?? "").slice(0, 200),
    statuses: list(params.get("status")).filter((value): value is InvoiceStatus =>
      (ALL_STATUSES as string[]).includes(value),
    ),
    customers: list(params.get("customer")).filter((value) => ID.test(value)),
    from: day(params.get("from")),
    to: day(params.get("to")),
    dueFrom: day(params.get("dueFrom")),
    dueTo: day(params.get("dueTo")),
    min: amount(params.get("min")),
    max: amount(params.get("max")),
    currency: /^[A-Z]{3}$/.test(currency) ? currency : "",
    type: DOCUMENT_TYPES.find((value) => value && value === params.get("type")) ?? "",
  };
}

/** Filters -> URL parameter values; empty ones are left out (undefined). */
export function filterParams(filters: ListFilters): Record<(typeof FILTER_PARAMS)[number], string> {
  return {
    q: filters.q.trim(),
    status: filters.statuses.join(","),
    customer: filters.customers.join(","),
    from: filters.from,
    to: filters.to,
    dueFrom: filters.dueFrom,
    dueTo: filters.dueTo,
    min: filters.min,
    max: filters.max,
    currency: filters.currency,
    type: filters.type,
  };
}

/** How many filters are set in the sheet (search and status live in the toolbar). */
export function sheetFilterCount(filters: ListFilters): number {
  return [
    filters.customers.length > 0,
    Boolean(filters.from || filters.to),
    Boolean(filters.dueFrom || filters.dueTo),
    Boolean(filters.min || filters.max),
    Boolean(filters.currency),
    Boolean(filters.type),
  ].filter(Boolean).length;
}

export function hasFilters(filters: ListFilters): boolean {
  return Boolean(filters.q.trim()) || filters.statuses.length > 0 || sheetFilterCount(filters) > 0;
}

// Search: every word must appear in one of these (case-insensitive substring, PocketBase `~`).
const SEARCH_FIELDS = [
  "invoiceNumber",
  "invoiceTitle",
  "customer.name",
  "customer.contactPerson",
  "customer.email",
  "items",
];
const MAX_WORDS = 6;

/**
 * The PocketBase filter for `filters`, as an expression with `{:placeholders}` and its params
 * (for pb.filter(), which escapes the values).
 */
export function filterExpression(filters: ListFilters): {
  expression: string;
  params: Record<string, string | number>;
} {
  const parts: string[] = [];
  const params: Record<string, string | number> = {};

  const words = filters.q.trim().split(/\s+/).filter(Boolean).slice(0, MAX_WORDS);
  words.forEach((word, index) => {
    const key = `q${index}`;
    params[key] = word;
    const fields = SEARCH_FIELDS.map((field) => `${field} ~ {:${key}}`);
    // Numbers also find amounts ("1440" or "1440.50").
    if (/^\d+([.,]\d+)?$/.test(word)) {
      params[`${key}n`] = word.replace(",", ".");
      fields.push(`total ~ {:${key}n}`);
    }
    parts.push(`(${fields.join(" || ")})`);
  });

  const anyOf = (field: string, values: string[], prefix: string) => {
    if (!values.length) return;
    values.forEach((value, index) => (params[`${prefix}${index}`] = value));
    parts.push(`(${values.map((_, index) => `${field} = {:${prefix}${index}}`).join(" || ")})`);
  };
  anyOf("status", filters.statuses, "s");
  anyOf("customer", filters.customers, "c");

  const range = (field: string, from: string, to: string, prefix: string) => {
    if (from) {
      params[`${prefix}From`] = `${from} 00:00:00.000Z`;
      parts.push(`${field} >= {:${prefix}From}`);
    }
    if (to) {
      params[`${prefix}To`] = `${to} 23:59:59.999Z`;
      parts.push(`${field} <= {:${prefix}To}`);
    }
  };
  range("issueDate", filters.from, filters.to, "issued");
  range("dueDate", filters.dueFrom, filters.dueTo, "due");

  if (filters.min) {
    params.min = Number(filters.min);
    parts.push("total >= {:min}");
  }
  if (filters.max) {
    params.max = Number(filters.max);
    parts.push("total <= {:max}");
  }
  if (filters.currency) {
    params.currency = filters.currency;
    parts.push("currency = {:currency}");
  }
  if (filters.type === "credit_notes") parts.push('creditFor != ""');
  if (filters.type === "invoices") parts.push('creditFor = ""');

  return { expression: parts.join(" && "), params };
}

export type DatePreset = "thisMonth" | "lastMonth" | "thisQuarter" | "thisYear" | "lastYear";
export const DATE_PRESETS: DatePreset[] = [
  "thisMonth",
  "lastMonth",
  "thisQuarter",
  "thisYear",
  "lastYear",
];

const pad = (n: number) => String(n).padStart(2, "0");
const toDay = (date: Date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

/** First and last day of a preset period, relative to `now`. */
export function presetRange(preset: DatePreset, now = new Date()): { from: string; to: string } {
  const year = now.getFullYear();
  const month = now.getMonth();
  const span = (start: Date, end: Date) => ({ from: toDay(start), to: toDay(end) });
  switch (preset) {
    case "thisMonth":
      return span(new Date(year, month, 1), new Date(year, month + 1, 0));
    case "lastMonth":
      return span(new Date(year, month - 1, 1), new Date(year, month, 0));
    case "thisQuarter": {
      const start = month - (month % 3);
      return span(new Date(year, start, 1), new Date(year, start + 3, 0));
    }
    case "thisYear":
      return span(new Date(year, 0, 1), new Date(year, 11, 31));
    case "lastYear":
      return span(new Date(year - 1, 0, 1), new Date(year - 1, 11, 31));
  }
}

/** The preset a from/to pair matches, if any (shown selected in the sheet). */
export function matchingPreset(from: string, to: string, now = new Date()): DatePreset | "" {
  if (!from || !to) return "";
  return (
    DATE_PRESETS.find((preset) => {
      const range = presetRange(preset, now);
      return range.from === from && range.to === to;
    }) ?? ""
  );
}
